/**
 * @package   FlorenceEGI — EGI — Variant pricing (M-EGI-281)
 * @author    Padmin D. Curtis (AI Partner OS3.0-Compliant) for Fabio Cherici
 * @version   2.0.0
 * @date      2026-06-17
 * @purpose   Calcolo PURO (no-DOM, no-any) del prezzo di una combinazione, condiviso
 *            tra il selettore VERSIONI pubblico (variant-selector) e l'eventuale stima
 *            client. SPECCHIA esattamente il backend
 *            App\Models\Concerns\HasVariants::computeVariantPrice
 *            (ratio -> round(base x prod(price_ratio), 2), NULL = 1.0; explicit -> base;
 *            override prezzo pieno vince sempre). Estratto per unit-test (Vitest).
 *
 * @note M-EGI-281 STEP A: la UI di configurazione "Versioni" è una LISTA semplice
 *       (versions-list-model). Questo modulo resta SOLO per il calcolo prezzo condiviso
 *       col lato compratore (variant-selector importa computeComboPrice/PricingMode).
 *
 * @contract Il prezzo materializzato dal server resta l'AUTORITA: qui si calcola SOLO
 *           una stima client. Override 'explicit' = prezzo pieno, il moltiplicatore non si applica.
 */

/** Modalita di prezzo per-opera (backend: variant_pricing_mode). */
export type PricingMode = "explicit" | "ratio";

/** Arrotonda a 2 decimali come round($x, 2) di PHP (half-up sui positivi). */
function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Prezzo calcolato di una combinazione. SPECCHIA HasVariants::computeVariantPrice:
 *  - mode='ratio' -> round(base x prod(price_ratio), 2), priceRatio null = 1.0
 *  - altrimenti   -> round(base, 2)
 * L'override (prezzo pieno) vince sempre quando presente.
 *
 * @param base   prezzo base dell'opera (campo price del Passo 1)
 * @param mode   modalita di pricing per-opera
 * @param ratios moltiplicatori dei valori-asse della combinazione (in ordine asse)
 * @param override prezzo pieno inserito a mano (null = nessun override)
 */
export function computeComboPrice(
  base: number,
  mode: PricingMode,
  ratios: readonly (number | null)[],
  override: number | null,
): number {
  if (override !== null) return round2(override);
  if (mode !== "ratio") return round2(base);

  let product = 1;
  for (const r of ratios) product *= r === null ? 1 : r;
  return round2(base * product);
}
